import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Layers, Circle, Square, ChevronDown, FileText, Shuffle, ArrowLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getAssignedAssessmentQuestions } from "../../Services/CompanyServices";
import RichTextEditor from "../../../../Utility/RichTextEditor";
import HRAssessmentNavTabs from "../../../../Utility/HRAssessmentNavTabs";
import { sanitizeHtml, DESCRIPTION_RICH_CONTENT_STYLES } from "../../../../Utility/sanitizeHtml";

const DEFAULT_SECTIONS = ["Section A", "Section B"];
const SECTIONS_WITHOUT_QUALIFICATION = ["Section B"];
const SECTIONS_WITH_CASE_STUDY = ["Section B"];

const caseKey = (channel, caseName) => `${channel}::${caseName}`;

const stripHtml = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body.textContent || "").trim();
};

const CaseTabs = ({ options, value, onSelect }) => {
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-1">
      {options.map((option) => {
        const active = value === option;
        return (
          <div key={option}
            className={`group flex flex-shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all ${active
              ? "bg-white text-[#7f35b2] shadow-sm ring-1 ring-[#7f35b2]/20"
              : "bg-gray-100 text-gray-500 hover:text-gray-700"
              }`}
          >
            <button type="button" onClick={() => onSelect(option)} className="whitespace-nowrap">
              {option}
            </button>
          </div>
        );
      })}
    </div>
  );
};

const OptionIndicator = ({ optionType, index }) => {
  if (optionType === "Multiple Choice") {
    return <Circle size={16} className="flex-shrink-0 text-gray-300" aria-hidden="true" />;
  }
  if (optionType === "Check Boxes") {
    return <Square size={16} className="flex-shrink-0 text-gray-300" aria-hidden="true" />;
  }
  if (optionType === "Dropdown" || optionType === "Rank") {
    return (
      <span className="w-5 flex-shrink-0 text-center text-sm font-medium text-gray-400">
        {index + 1}.
      </span>
    );
  }
  return null;
};

const HRAssessmentPreview = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [channels, setChannels] = useState([]);
  const channelNames = useMemo(() => channels.map((c) => c.value), [channels]);

  const [selectedSection, setSelectedSection] = useState(DEFAULT_SECTIONS[0]);
  const [selectedChannel, setSelectedChannel] = useState("");
  const [expandedSection, setExpandedSection] = useState(DEFAULT_SECTIONS[0]);

  const [channelCases, setChannelCases] = useState({});
  const [selectedCase, setSelectedCase] = useState(null);
  const [pendingScrollId, setPendingScrollId] = useState(null);

  const [caseStudyContent, setCaseStudyContent] = useState({});

  const location = useLocation();
  const navigate = useNavigate();
  const { assessmentUID, assessmentName, description } = location.state || {};
  const questionRefs = useRef({});

  useEffect(() => {
    if (assessmentUID) {
      loadSurveyQuestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentUID]);

  useEffect(() => {
    if (!pendingScrollId) return;
    const questionCard = questionRefs.current[pendingScrollId];
    if (questionCard) {
      questionCard.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setPendingScrollId(null);
    }
  }, [questions, pendingScrollId]);

  const loadSurveyQuestions = async () => {
    try {
      setLoading(true);
      // Constructing request payload. Assuming the API needs surveyUID.
      const request = { surveyUID: assessmentUID };
      const questionResponse = await getAssignedAssessmentQuestions(request);

      if (questionResponse.data.status?.toLowerCase() === "success") {
        const extractedCaseContent = {};
        const extractedCasesByChannel = {};
        const extractedChannels = new Set();

        const loadedQuestions = (questionResponse.data.surveyDetails || []).map(
          (question) => {
            const caseName = question.caseTitle || null;
            const channelName = question.channel || "Default";
            extractedChannels.add(channelName);

            if (question.section === "Section B" && caseName) {
              if (!extractedCasesByChannel[channelName]) {
                extractedCasesByChannel[channelName] = new Set();
              }
              extractedCasesByChannel[channelName].add(caseName);

              const key = caseKey(channelName, caseName);
              if (question.caseDescription && !extractedCaseContent[key]) {
                extractedCaseContent[key] = question.caseDescription;
              }
            }

            return {
              QuestionID: question.questionID,
              QuestionName: question.questionName,
              OptionType: question.optionType,
              Options: (question.options || []).map((option) => ({
                optionID: option.optionID,
                optionName: option.optionName,
                isDisqualifying: option.isDisqualifying ?? false,
                rank: option.answerRank ?? "",
              })),
              IsRequired: question.isRequired === 1,
              IsJumbleOptions: question.isJumbleOptions === 1,
              Status: question.status === 1,
              QuestionSequence: question.questionSequence,
              Section: DEFAULT_SECTIONS.includes(question.section) ? question.section : DEFAULT_SECTIONS[0],
              Qualification: question.experienceType || "Experienced",
              ChannelType: channelName,
              CaseTitle: question.caseTitle,
              CaseDescription: question.caseDescription,
              CaseType: caseName,
            };
          },
        );

        loadedQuestions.sort((first, second) => first.QuestionSequence - second.QuestionSequence);
        setQuestions(loadedQuestions);

        const chs = Array.from(extractedChannels).map(c => ({ value: c, id: c }));
        if (chs.length === 0) chs.push({ value: "Default", id: "Default" });
        setChannels(chs);
        const initialChannel = chs[0].value;
        setSelectedChannel(initialChannel);

        const newChannelCases = {};
        chs.forEach((c) => {
          newChannelCases[c.value] = Array.from(extractedCasesByChannel[c.value] || []);
        });

        setChannelCases(newChannelCases);
        setCaseStudyContent(extractedCaseContent);

        const activeChannelCases = newChannelCases[initialChannel] || [];
        if (activeChannelCases.length > 0) {
          setSelectedCase(activeChannelCases[0]);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const scrollToQuestion = (questionID) => {
    const questionCard = questionRefs.current[questionID];
    if (questionCard) {
      questionCard.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleSidebarItemClick = (section, questionID) => {
    if (section !== selectedSection) {
      setSelectedSection(section);
      setPendingScrollId(questionID);
    } else {
      scrollToQuestion(questionID);
    }
  };

  const toggleSection = (section) => {
    setSelectedSection(section);
    setExpandedSection((current) => (current === section ? null : section));
  };

  const sectionCounts = DEFAULT_SECTIONS.reduce((counts, section) => {
    counts[section] = questions.filter((question) => question.Section === section).length;
    return counts;
  }, {});

  const sectionQuestions = selectedSection
    ? questions.filter((question) => {
      if (question.Section !== selectedSection || question.ChannelType !== selectedChannel) return false;
      if (selectedSection === "Section B") return question.CaseType === selectedCase;
      return true;
    })
    : [];

  const activeCaseList = channelCases[selectedChannel] || [];
  const showCaseStudy = SECTIONS_WITH_CASE_STUDY.includes(selectedSection) && Boolean(selectedCase);

  return (
    <div className="min-h-screen bg-[#f7f8fa] p-0.5">
      <div className="mx-auto max-w-[88rem]">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-3 flex items-center gap-1.5 rounded-lg bg-[#7f35b2] px-3.5 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#6d2b98]"
        >
          <ArrowLeft size={16} />
          Back
        </button>
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">{assessmentName}</h1>

        <HRAssessmentNavTabs
          active="preview"
          assessmentUID={assessmentUID}
          assessmentName={assessmentName}
          description={description}
        />

        <div className="flex gap-3 pb-6">
          <aside className="sticky top-[72px] flex h-[calc(100vh-140px)] w-[320px] flex-shrink-0 flex-col gap-4 overflow-hidden rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            {loading ? <div className="animate-pulse flex flex-col gap-4">
              <div className="h-16 bg-gray-100 rounded-xl"></div>
              <div className="h-64 bg-gray-100 rounded-xl"></div>
            </div> : (
              <>
                <div className="rounded-xl bg-gradient-to-br from-[#7f35b2]/10 to-[#7f35b2]/5 p-4">
                  <div className="mb-1 flex items-start justify-between gap-2">
                    <h2 className="text-base font-semibold leading-tight text-gray-900 line-clamp-1">{assessmentName}</h2>
                    <span className="flex-shrink-0 rounded-full bg-[#7f35b2]/20 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[#7f35b2]">
                      Preview
                    </span>
                  </div>
                  <style>{DESCRIPTION_RICH_CONTENT_STYLES}</style>
                  <div
                    className="description-rich-content text-xs text-gray-600 line-clamp-5"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
                  />
                </div>

                <div className="flex-1 overflow-y-auto pr-1">
                  <div className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Sections</div>
                  <div className="space-y-2">
                    {DEFAULT_SECTIONS.map((section) => {
                      const isSelected = selectedSection === section;
                      const isExpanded = expandedSection === section;
                      const sectionList = questions.filter((question) => {
                        if (question.Section !== section || question.ChannelType !== selectedChannel) return false;
                        if (section === "Section B") return question.CaseType === selectedCase;
                        return true;
                      });

                      return (
                        <div
                          key={section}
                          className={`rounded-xl border transition-colors ${isSelected ? "border-[#7f35b2]/30 bg-[#7f35b2]/5" : "border-transparent bg-gray-50"
                            }`}
                        >
                          <button
                            type="button"
                            onClick={() => toggleSection(section)}
                            aria-expanded={isExpanded}
                            className="flex w-full items-center justify-between px-3 py-2.5"
                          >
                            <div className="flex items-center gap-2">
                              <Layers size={14} className={isSelected ? "text-[#7f35b2]" : "text-gray-400"} />
                              <span className={`text-sm font-medium ${isSelected ? "text-[#7f35b2]" : "text-gray-700"}`}>
                                {section}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${isSelected ? "bg-[#7f35b2] text-white" : "bg-gray-200 text-gray-600"
                                }`}>
                                {sectionCounts[section] || 0}
                              </span>
                              <motion.span animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }} className="text-gray-400">
                                <ChevronDown size={14} />
                              </motion.span>
                            </div>
                          </button>

                          <AnimatePresence initial={false}>
                            {isExpanded && (
                              <motion.div
                                key="content"
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                                className="max-h-[270px] overflow-y-auto"
                              >
                                <div className="px-2 pb-2">
                                  <div className="space-y-1">
                                    {sectionList.map((question, index) => (
                                      <div
                                        key={question.QuestionID}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => handleSidebarItemClick(section, question.QuestionID)}
                                        className="group flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-white"
                                      >
                                        <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md bg-gray-100 text-[10px] font-semibold text-gray-500">
                                          {index + 1}
                                        </span>
                                        <span className="line-clamp-1 flex-1 text-xs text-gray-600">
                                          {stripHtml(question.QuestionName) || "Untitled Question"}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </aside>

          <main className="min-w-0 flex-1 space-y-3">
            {loading ? <div className="animate-pulse h-64 bg-white rounded-2xl"></div> : (
              <>
                <div className="flex items-start justify-between gap-4 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
                  <div>
                    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-[#7f35b2]">
                      <span>{selectedSection}</span>
                      <span>·</span>
                      <span className="text-gray-600">{selectedChannel}</span>
                      {selectedSection === "Section B" && selectedCase && (
                        <>
                          <span>·</span>
                          <span className="text-amber-600">{selectedCase}</span>
                        </>
                      )}
                    </div>
                    <h1 className="mt-1 text-2xl font-bold text-gray-900">Questions</h1>
                    <p className="mt-1 text-sm text-gray-500">
                      {sectionQuestions.length} question{sectionQuestions.length === 1 ? "" : "s"} in this{" "}
                      {selectedSection === "Section B" ? "case" : "channel"}.
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between rounded-2xl border border-gray-100 bg-white p-2 shadow-sm">
                  <div className="flex w-full items-center gap-2 sm:w-auto">
                    {channelNames.map((channel) => {
                      const isActive = selectedChannel === channel;
                      const channelQuestionCount = questions.filter(
                        (q) => q.Section === selectedSection && q.ChannelType === channel
                      ).length;

                      return (
                        <button key={channel} type="button" onClick={() => setSelectedChannel(channel)}
                          className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-all sm:flex-initial sm:justify-start ${isActive
                            ? "bg-[#7f35b2] text-white shadow-sm"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                            }`}
                        >
                          <span>{channel}</span>
                          <span
                            className={`ml-1 rounded-full px-2 py-0.5 text-xs font-semibold ${isActive
                              ? "bg-white/20 text-white"
                              : "bg-gray-100 text-gray-500"
                              }`}
                          >
                            {channelQuestionCount}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {selectedSection === "Section B" && activeCaseList.length > 0 && (
                  <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                    <div className="mb-3 flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
                        <FileText size={13} className="text-amber-500" />
                        <span>Cases for {selectedChannel}</span>
                      </div>
                    </div>
                    <CaseTabs
                      value={selectedCase}
                      options={activeCaseList}
                      onSelect={setSelectedCase}
                    />
                  </div>
                )}

                {showCaseStudy && caseStudyContent[caseKey(selectedChannel, selectedCase)] && (
                  <section className="rounded-2xl border border-amber-200/60 bg-gradient-to-br from-amber-50/60 to-white p-6 shadow-sm">
                    <div className="mb-4 flex items-start gap-3">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                        <FileText size={18} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900">
                          Case Study Content — <span className="text-amber-700">{selectedCase}</span>
                        </h3>
                      </div>
                    </div>
                    <RichTextEditor
                      key={caseKey(selectedChannel, selectedCase)}
                      initialContent={caseStudyContent[caseKey(selectedChannel, selectedCase)] || ""}
                      editable={false}
                    />
                  </section>
                )}

                {sectionQuestions.map((question, index) => {
                  const showQualification = !SECTIONS_WITHOUT_QUALIFICATION.includes(question.Section);

                  return (
                    <article
                      key={question.QuestionID}
                      id={`question-${question.QuestionID}`}
                      ref={(element) => {
                        if (element) {
                          questionRefs.current[question.QuestionID] = element;
                        }
                      }}
                      tabIndex={-1}
                      className="scroll-mt-24 rounded-2xl border border-gray-100 bg-white shadow-sm"
                    >
                      <div className="flex items-center justify-between border-b border-gray-50 px-6 py-3">
                        <div className="flex items-center gap-2 text-xs font-medium text-gray-400">
                          <span className="rounded-full bg-[#7f35b2]/10 px-2.5 py-1 text-xs font-semibold text-[#7f35b2]">
                            Q{index + 1}
                          </span>
                          <span>
                            {question.Section} · {question.ChannelType} · {question.OptionType} {showQualification ? ` · ${question.Qualification}` : ""}
                          </span>
                          {question.IsRequired && (
                            <span className="ml-0.5 text-red-500 text-xl leading-none" title="Required">*</span>
                          )}
                          {question.IsJumbleOptions && (
                            <span
                              className="ml-1 flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-600"
                              title="Options are shown to candidates in randomized order"
                            >
                              <Shuffle size={11} />
                              Jumbled
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="px-6 pb-2 pt-4">
                        <RichTextEditor
                          initialContent={question.QuestionName}
                          editable={false}
                        />
                      </div>

                      <div className="space-y-6 px-6 pb-6">
                        {(question.OptionType === "Short Answer" || question.OptionType === "Paragraph") && (
                          <div className="rounded-2xl border border-gray-100 bg-slate-50 p-5">
                            {question.OptionType === "Short Answer" ? (
                              <input readOnly disabled placeholder="Short answer" className="w-full rounded-lg border bg-white px-3 py-2 text-sm" />
                            ) : (
                              <textarea disabled rows={4} placeholder="Paragraph" className="w-full rounded-lg border bg-white px-3 py-2 text-sm" />
                            )}
                          </div>
                        )}

                        {["Multiple Choice", "Check Boxes", "Dropdown", "Rank"].includes(question.OptionType) && (
                          <div>
                            <div className="space-y-2">
                              {question.Options.map((option, optionIndex) => (
                                <div
                                  key={optionIndex}
                                  className="group flex items-center gap-3 rounded-lg px-3 py-2 transition-colors bg-gray-50/50"
                                >
                                  <OptionIndicator optionType={question.OptionType} index={optionIndex} />
                                  <input
                                    readOnly
                                    value={option.optionName}
                                    className="flex-1 bg-transparent text-sm outline-none"
                                  />
                                  {question.OptionType === "Rank" && (
                                    <input
                                      disabled
                                      type="number"
                                      value={option.rank ?? ""}
                                      className="w-20 flex-shrink-0 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-center text-sm outline-none"
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </article>
                  );
                })}

                {sectionQuestions.length === 0 && (
                  <div className="rounded-3xl border border-dashed border-gray-300 bg-white p-8 text-center text-gray-500">
                    No questions available.
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default HRAssessmentPreview;
